/*
 * Clients: the list (tiles, chips, search, tick boxes with bulk changes and Undo), one client's page (read first: contacts,
 * folded properties, jobs, visits, notes, files; Deactivate and Merge sheets), and the client form.
 */
// Clients: the list, sorting, the tiles.
import { BULK_LIMIT, type ClientRow, type ClientSummary, INACTIVE_REASON_WORDS } from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, downloadFile } from "../../api/client";
import { type BulkInput, type ClientsQuery, useBulkClients, useClientPages, useClientSummary } from "../../api/hooks";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { InputText } from "../../components/fields";
import { daysAway, dayWords, moneyExact, phoneText, plural, shortDate, todayIn, trend } from "../../components/format";
import { Icon } from "../../components/Icon";
import { Menu, Modal, useToast } from "../../components/overlays";
import { Button, Chip, EmptyState, IconButton, LoadError, Page, StatusLabel } from "../../components/parts";
import { errText } from "../../components/work";

export const LEAD_WORDS: Record<string, string> = {
  google: "Google search",
  lsa: "Google ads (LSA)",
  facebook: "Facebook",
  referral: "Referral",
  website: "Website",
  yard_sign: "Yard sign",
  repeat: "Past client",
  jobber: "Moved from Jobber",
  other: "Other",
};

// List ---------------------------------------------------------------------------------------------------------------

type SortKey = NonNullable<ClientsQuery["sort"]>;

/** Column key to the server's sort, for the sortable headers. */
const SORT_OF: Record<string, SortKey> = {
  name: "name",
  next: "next_visit",
  activity: "last_activity",
  lifetime: "lifetime",
};

const USUAL_DIR: Record<SortKey, "asc" | "desc"> = {
  name: "asc",
  next_visit: "asc",
  newest: "desc",
  last_activity: "desc",
  lifetime: "desc",
};

const SORT_WORDS: [SortKey, string][] = [
  ["name", "A to Z"],
  ["newest", "Newest first"],
  ["next_visit", "Next visit first"],
  ["last_activity", "Last activity"],
  ["lifetime", "Paid the most"],
];

type Show = NonNullable<ClientsQuery["show"]>;

const SHOW_WORDS: [Show, string][] = [
  ["plan", "On a plan"],
  ["nothing_booked", "Nothing booked"],
  ["at_risk", "At risk"],
];

/** The company's date for a moment, to say "4 days ago" on the company's clock. */
const dayIn = (iso: string, tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));

/**
 * The client list (2026-10-29, the best of Jobber's and the old Steward's): tiles that say how the client base is doing
 * and filter the list, then one table with what the office looks for (who, where, tags, next visit, last activity, what
 * they paid us) and an at risk line under the name.
 */
export function ClientsList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState<ClientsQuery>({ status: "active", sort: "last_activity" });
  const [typed, setTyped] = useState("");
  const list = useClientPages(company.id, { ...q, q: typed.trim() || undefined });
  const summary = useClientSummary(company.id);
  const bulk = useBulkClients();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ input: BulkInput; message: string; token: string; undo: boolean } | null>(null);
  const [tagging, setTagging] = useState(false);
  const [tag, setTag] = useState("");
  const canBulk = can("client.bulk");
  const s = summary.data;
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const today = todayIn(company.timezone);
  const sort = q.sort ?? "last_activity";
  const dir = q.dir ?? USUAL_DIR[sort];
  const narrowed = typed.trim() !== "" || q.tag !== undefined || q.show !== undefined;
  const total =
    !s || narrowed ? undefined : q.status === "inactive" ? s.inactive : q.status === "all" ? s.active + s.inactive : s.active;
  const pick = (next: ClientsQuery) => {
    setQ(next);
    setTicked(new Set());
  };
  const showOnly = (v: Show) => pick({ ...q, show: q.show === v ? undefined : v, ...(q.show === v ? {} : { status: "active" }) });

  const opposite = (i: BulkInput): BulkInput => ({
    ids: i.ids,
    action:
      i.action === "deactivate" ? "reactivate" : i.action === "reactivate" ? "deactivate" : i.action === "tag" ? "untag" : "tag",
    ...(i.tag ? { tag: i.tag } : {}),
    ...(i.action === "reactivate" ? { reason: "other" as const } : {}),
  });
  const words = (i: BulkInput, n: number) =>
    i.action === "deactivate"
      ? `${plural(n, "client")} set to not active`
      : i.action === "reactivate"
        ? `${plural(n, "client")} active again`
        : i.action === "tag"
          ? `Tag ${i.tag ?? ""} added to ${plural(n, "client")}`
          : `Tag ${i.tag ?? ""} taken off ${plural(n, "client")}`;
  /** Runs a bulk change. Over the limit the server asks to confirm first; the confirm step shows its words. */
  const run = (input: BulkInput, undo = false) =>
    bulk.mutate(input, {
      onSuccess: (a) => {
        setConfirm(null);
        setTicked(new Set());
        if (undo) toast("Undone");
        else toast(words(input, a.done), { undo: () => run(opposite(input), true) });
      },
      onError: (e) => {
        if (e instanceof ApiError && e.code === "confirm_needed" && typeof e.extra.confirm === "string")
          setConfirm({ input, message: e.message, token: e.extra.confirm, undo });
        else toast(errText(e), { kind: "error" });
      },
    });
  const ids = [...ticked];

  return (
    <Page
      title="Clients"
      primaryAction={
        can("client.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/clients/new" })}>
            New client
          </Button>
        ) : null
      }
      moreActions={
        canBulk ? (
          <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
            {(close) => (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void navigate({ to: "/settings/data" });
                  }}
                >
                  Import clients
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void downloadFile("export/clients.csv", "clients.csv").then((ok) => {
                      if (!ok) toast("The preview has no server, so there is no file to download.");
                    });
                  }}
                >
                  Export clients
                </button>
              </>
            )}
          </Menu>
        ) : null
      }
    >
      <ClientTiles s={s} show={q.show} onShow={showOnly} />
      <DataTable<ClientRow>
        title={q.show ? (SHOW_WORDS.find(([v]) => v === q.show)?.[1] ?? "Clients") : "All clients"}
        rows={rows}
        total={total}
        rowKey={(c) => c.id}
        loading={list.isFetching && !list.isFetchingNextPage}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Name, phone, email or street" }}
        onRowClick={(c) => void navigate({ to: "/clients/$id", params: { id: c.id } })}
        {...(canBulk ? { selected: ticked, onSelect: setTicked } : {})}
        serverSort={{
          keys: Object.keys(SORT_OF),
          key: Object.keys(SORT_OF).find((k) => SORT_OF[k] === sort) ?? "",
          dir,
          onSort: (k) => {
            const next = SORT_OF[k] ?? "name";
            pick(next === sort ? { ...q, dir: dir === "asc" ? "desc" : "asc" } : { ...q, sort: next, dir: undefined });
          },
        }}
        footer={
          list.hasNextPage ? (
            <div className="table-more">
              <Button type="tertiary" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Show more
              </Button>
            </div>
          ) : null
        }
        bulk={
          <>
            <Button type="secondary" size="small" icon="tag" onClick={() => setTagging(true)}>
              Add tag
            </Button>
            {q.status === "inactive" ? (
              <Button size="small" loading={bulk.isPending} onClick={() => run({ ids, action: "reactivate" })}>
                Make active
              </Button>
            ) : (
              <Button
                size="small"
                variation="destructive"
                loading={bulk.isPending}
                onClick={() => run({ ids, action: "deactivate", reason: "other" })}
              >
                Set not active
              </Button>
            )}
          </>
        }
        tools={
          <>
            <div className="chips">
              {(
                [
                  ["active", "Active", s?.active],
                  ["inactive", "Not active", s?.inactive],
                  ["all", "All", s ? s.active + s.inactive : undefined],
                ] as const
              ).map(([v, label, count]) => (
                <Chip
                  key={v}
                  pressed={q.status === v}
                  onClick={() => pick({ ...q, status: v, ...(v === "active" ? {} : { show: undefined }) })}
                  {...(count !== undefined ? { count } : {})}
                >
                  {label}
                </Chip>
              ))}
              <Chip pressed={q.show === "nothing_booked"} onClick={() => showOnly("nothing_booked")}>
                Nothing booked
              </Chip>
              {s?.tags.length ? (
                <Menu
                  button={({ toggle }) => (
                    <Chip pressed={Boolean(q.tag)} onClick={toggle}>
                      {q.tag ? `Tag ${q.tag}` : "Tag"}
                    </Chip>
                  )}
                >
                  {(close) => (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          pick({ ...q, tag: undefined });
                          close();
                        }}
                      >
                        Every tag
                      </button>
                      {s.tags.map((t) => (
                        <button
                          key={t.tag}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            pick({ ...q, tag: t.tag });
                            close();
                          }}
                        >
                          {t.tag} <span className="muted num">{t.count}</span>
                        </button>
                      ))}
                    </>
                  )}
                </Menu>
              ) : null}
            </div>
            <Menu
              align="right"
              button={({ toggle }) => (
                <Button type="tertiary" size="small" icon="list" onClick={toggle}>
                  {SORT_WORDS.find(([v]) => v === sort)?.[1] ?? "Last activity"}
                </Button>
              )}
            >
              {(close) =>
                SORT_WORDS.map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      pick({ ...q, sort: v, dir: undefined });
                      close();
                    }}
                  >
                    {label}
                  </button>
                ))
              }
            </Menu>
          </>
        }
        empty={
          q.show === "at_risk" ? (
            "Nobody looks at risk right now."
          ) : q.show ? (
            "No clients match this."
          ) : q.status === "inactive" ? (
            "No clients are set to not active."
          ) : (
            <EmptyState icon="clients" title="No clients yet">
              Add your first client, or move them over from Jobber.
            </EmptyState>
          )
        }
        rowClass={(c) => (c.active ? undefined : "inactive")}
        columns={[
          {
            key: "name",
            header: "Name",
            render: (c) => (
              <div className="name-cell">
                <b>{c.name}</b>
                <span className="muted">{c.isCompany ? "Business" : (phoneText(c.phone) ?? c.email ?? "")}</span>
                {c.atRisk && c.active ? (
                  <span className="at-risk" title="At risk">
                    <Icon name="warning" size={14} />
                    {c.atRisk}
                  </span>
                ) : null}
              </div>
            ),
          },
          { key: "address", header: "Address", render: (c) => c.address ?? <span className="muted">No address</span> },
          {
            key: "service",
            header: "Service",
            render: (c) =>
              c.service ? (
                <div className="name-cell">
                  <span>{c.category ?? c.service}</span>
                  {c.category && c.category.toLowerCase() !== c.service.toLowerCase() ? (
                    <span className="muted">{c.service}</span>
                  ) : null}
                </div>
              ) : (
                <span className="muted">No jobs yet</span>
              ),
          },
          {
            key: "tags",
            header: "Tags",
            render: (c) =>
              c.tags.length ? (
                <div className="tag-list">
                  {c.tags.slice(0, 2).map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                  {c.tags.length > 2 ? (
                    <span className="tag" title={c.tags.slice(2).join(", ")}>
                      +{c.tags.length - 2}
                    </span>
                  ) : null}
                </div>
              ) : null,
          },
          {
            key: "next",
            header: "Next visit",
            render: (c) =>
              c.active ? (
                c.nextVisit ? (
                  <span title={shortDate(c.nextVisit)}>{dayWords(c.nextVisit, today)}</span>
                ) : c.lastVisit ? (
                  // Work done but nothing next (a one-time clean): say when, so it does not read as never served.
                  <span className="muted" title={`Nothing booked after ${shortDate(c.lastVisit)}`}>
                    Last visit {dayWords(c.lastVisit, today)}
                  </span>
                ) : (
                  <span className="muted">Nothing booked</span>
                )
              ) : (
                <StatusLabel status="inactive">
                  {c.inactiveReason ? INACTIVE_REASON_WORDS[c.inactiveReason] : "Not active"}
                </StatusLabel>
              ),
          },
          {
            key: "activity",
            header: "Last activity",
            render: (c) =>
              c.lastActivity ? (
                <span title={new Date(c.lastActivity.at).toLocaleString()}>
                  {c.lastActivity.what}{" "}
                  <span className="muted">{daysAway(dayIn(c.lastActivity.at, company.timezone), today).toLowerCase()}</span>
                </span>
              ) : (
                <span className="muted">Nothing yet, added {shortDate(c.createdAt.slice(0, 10))}</span>
              ),
          },
          {
            key: "lifetime",
            header: "Lifetime",
            align: "right",
            render: (c) => <span className="num">{moneyExact(c.lifetimeCents)}</span>,
          },
        ]}
      />
      <Modal
        title="Add a tag"
        open={tagging}
        onClose={() => setTagging(false)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setTagging(false)}>
              Cancel
            </Button>
            <Button
              disabled={!tag.trim()}
              loading={bulk.isPending}
              onClick={() => {
                setTagging(false);
                run({ ids, action: "tag", tag: tag.trim() });
                setTag("");
              }}
            >
              Add tag
            </Button>
          </>
        }
      >
        <InputText
          label="Tag"
          value={tag}
          autoFocus
          placeholder="Weekly"
          onChange={(e) => setTag(e.target.value)}
          description={`Goes on ${plural(ids.length, "client")}.`}
        />
      </Modal>
      <Modal
        title={`Change ${plural(confirm?.input.ids.length ?? 0, "client")}?`}
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variation={confirm?.input.action === "deactivate" ? "destructive" : "work"}
              loading={bulk.isPending}
              onClick={() => confirm && run({ ...confirm.input, confirm: confirm.token }, confirm.undo)}
            >
              Yes, change {confirm?.input.ids.length ?? 0}
            </Button>
          </>
        }
      >
        <p>{confirm?.message}</p>
        <p className="muted">More than {BULK_LIMIT} at once always asks first, so a slip does not change the whole list.</p>
      </Modal>
    </Page>
  );
}

/**
 * The four tiles: new leads and new clients next to the 30 days before (Jobber), how many are on a plan (the old Steward's
 * "converted to recurring"), and who may leave. The last two filter the list.
 */
function ClientTiles({ s, show, onShow }: { s: ClientSummary | undefined; show: Show | undefined; onShow: (v: Show) => void }) {
  const wait = <span className="skeleton inline" />;
  const leads = s ? trend(s.leads30, s.leadsBefore30) : null;
  const made = s ? trend(s.new30, s.newBefore30) : null;
  const pct = s?.active ? Math.round((s.onPlan / s.active) * 100) : 0;
  return (
    <div className="tiles">
      <div className="tile">
        <span className="label">New leads</span>
        <span className="value">{s ? s.leads30 : wait}</span>
        <span className="hint">Past 30 days, in the Pipeline</span>
        {leads ? <span className={`hint ${leads.cls}`}>{leads.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">New clients</span>
        <span className="value">{s ? s.new30 : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${String(s.newThisYear)} this year` : ""}</span>
        {made ? <span className={`hint ${made.cls}`}>{made.text}</span> : null}
      </div>
      <button type="button" className="tile tile-button" aria-pressed={show === "plan"} onClick={() => onShow("plan")}>
        <span className="label">On a plan</span>
        <span className="value">{s ? `${String(pct)}%` : wait}</span>
        <span className="hint">{s ? `${String(s.onPlan)} of ${String(s.active)} active clients repeat` : ""}</span>
      </button>
      <button type="button" className="tile tile-button" aria-pressed={show === "at_risk"} onClick={() => onShow("at_risk")}>
        <span className="label">At risk</span>
        <span className={`value${s?.atRisk ? " critical" : ""}`}>{s ? s.atRisk : wait}</span>
        <span className="hint">Complaints, low ratings, late bills, a crew that keeps changing</span>
      </button>
    </div>
  );
}

// One client ---------------------------------------------------------------------------------------------------------
